import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { Editor, Element, Transforms } from "slate";
import { useSlateStatic } from "slate-react";

import { IReplyPaths, ISuggestedPrompt } from "interfaces/entities/ticket";
import { ApiManager } from "services/api";
import { IGenerateReplySuggestionResponse, ReplySuggestionRating } from "services/api/ticket/interfaces";
import { getTicket } from "store/views/tickets/selectors";

import { RichEditorElement } from "../_components/rich-editor/types";

export enum Stage {
  Initial,
  ChooseSuggestion,
  Error,
  ReplyApplied,
}

export interface IUseReplySuggestionsReturn {
  generatePrompts: () => Promise<void>;
  generateAndApplySuggestion: (suggestionPath: ISuggestedPrompt, suggestionIndex?: number) => Promise<void>;
  replyPath: IReplyPaths | undefined;
  isLoadingPrompts: boolean;
  isLoadingReply: boolean;
  error: "prompts" | "reply" | "no-prompts" | undefined;
  appliedPrompt: ISuggestedPrompt | undefined;
  appliedSuggestion: IGenerateReplySuggestionResponse | undefined;
  reportPoorPrompts: () => Promise<void>;
  rateSuggestion: (rating: ReplySuggestionRating) => Promise<void>;
  isReportingPoorPrompts: boolean;
  hasReportedPoorPrompts: boolean;
  appliedSuggestionRating: ReplySuggestionRating | undefined;
  stage: Stage;
  hasClickedPreviewReplies: boolean;
  setHasClickedPreviewReplies: (value: boolean) => void;
  failedSuggestionIndex: number | undefined;
  resetToChooseSuggestion: () => void;
}

export const useReplySuggestions = (
  insertSignature: (preserveSelection?: boolean) => void,
  onReplySuggestionUse: (replySuggestionId: string) => void
): IUseReplySuggestionsReturn => {
  // RS should only be used on ticket pages, where we have a ticket object
  const { id, replyPaths: existingReplyPaths } = useSelector(getTicket)!;

  const editor = useSlateStatic();

  const hasSignature = (): boolean => {
    const signatureEntry = Array.from(
      Editor.nodes(editor, {
        at: [],
        mode: "all",
        match: (node): node is RichEditorElement =>
          !Editor.isEditor(node) && Element.isElement(node) && Boolean((node as RichEditorElement).signature),
      })
    )[0];

    return Boolean(signatureEntry);
  };

  const insertReply = (suggestion: IGenerateReplySuggestionResponse): void => {
    Transforms.insertNodes(editor, [{ text: suggestion.text }], {
      at: Editor.start(editor, []),
    });

    if (!hasSignature()) {
      insertSignature(true);
    }

    setTimeout(() => {
      Transforms.select(editor, {
        anchor: Editor.end(editor, [0, 0]),
        focus: Editor.end(editor, [0, 0]),
      });
    }, 0);
  };

  // Default to already existing reply paths, preventing user from generating new ones
  const [replyPath, setReplyPath] = useState<IReplyPaths | undefined>(existingReplyPaths);

  const [hasClickedPreviewReplies, setHasClickedPreviewReplies] = useState(false);
  const [isLoadingPrompts, setIsLoadingPrompts] = useState(false);
  const [isLoadingReply, setIsLoadingReply] = useState(false);
  const [isReportingPoorPrompts, setIsReportingPoorPrompts] = useState(false);

  const [hasReportedPoorPrompts, setHasReportedPoorPrompts] = useState(false);

  const [error, setError] = useState<"prompts" | "reply" | "no-prompts" | undefined>(undefined);
  const [appliedPrompt, setAppliedPrompt] = useState<ISuggestedPrompt | undefined>(undefined);
  const [appliedSuggestion, setAppliedSuggestion] = useState<IGenerateReplySuggestionResponse | undefined>(undefined);
  const [appliedSuggestionRating, setAppliedSuggestionRating] = useState<ReplySuggestionRating | undefined>(undefined);
  const [failedSuggestionIndex, setFailedSuggestionIndex] = useState<number | undefined>(undefined);

  if (existingReplyPaths && !replyPath && !hasClickedPreviewReplies) {
    // If new reply paths are received from WS, use them instead of the existing ones
    setReplyPath(existingReplyPaths);
  }

  const getStage = (): Stage => {
    if (!hasClickedPreviewReplies || isLoadingPrompts) {
      return Stage.Initial;
    }
    if ((hasClickedPreviewReplies && replyPath && !appliedSuggestion && !error) || isLoadingReply) {
      return Stage.ChooseSuggestion;
    }
    if (appliedSuggestion) {
      return Stage.ReplyApplied;
    }
    if (error) {
      return Stage.Error;
    }
    return Stage.Initial;
  };

  const stage = getStage();

  const generatePrompts = useCallback(async () => {
    if (replyPath) {
      return;
    }
    setIsLoadingPrompts(true);
    const response = await ApiManager.ticketApi.generateSuggestionPrompts(id);
    setIsLoadingPrompts(false);
    if (response.error) {
      setError("prompts");
      return;
    }

    if (response.result.length === 0) {
      setError("no-prompts");
      return;
    }

    setReplyPath(response.result[0]);
    setFailedSuggestionIndex(undefined);
  }, [id]);

  const generateAndApplySuggestion = useCallback(
    async (suggestionPath: ISuggestedPrompt, suggestionIndex?: number): Promise<void> => {
      if (!replyPath?.ID) {
        return;
      }

      setIsLoadingReply(true);
      setAppliedPrompt(suggestionPath);
      const response = await ApiManager.ticketApi.generateReplySuggestion(id, replyPath.ID, suggestionPath.pathID);
      setIsLoadingReply(false);
      if (response.error) {
        setError("reply");
        if (suggestionIndex !== undefined) {
          setFailedSuggestionIndex(suggestionIndex);
        }
        return;
      }

      insertReply(response.result);
      setAppliedSuggestion(response.result);
      setFailedSuggestionIndex(undefined);
      onReplySuggestionUse(response.result.ID);
    },
    [id, replyPath, insertReply, insertSignature, onReplySuggestionUse]
  );

  const reportPoorPrompts = useCallback(async (): Promise<void> => {
    if (!replyPath?.ID) {
      return;
    }
    setIsReportingPoorPrompts(true);

    await ApiManager.ticketApi.reportPoorPrompts(id, replyPath.ID);
    setIsReportingPoorPrompts(false);
    setHasReportedPoorPrompts(true);
  }, [id, replyPath]);

  const rateSuggestion = useCallback(
    async (rating: ReplySuggestionRating): Promise<void> => {
      if (!replyPath?.ID || !appliedSuggestion) {
        return;
      }
      setAppliedSuggestionRating(rating);
      await ApiManager.ticketApi.rateReplySuggestion(id, appliedSuggestion.ID, rating);
    },
    [id, replyPath, appliedSuggestion]
  );

  const resetToChooseSuggestion = useCallback((): void => {
    setError(undefined);
    setAppliedSuggestion(undefined);
    setAppliedPrompt(undefined);
    setAppliedSuggestionRating(undefined);
  }, []);

  return {
    generatePrompts,
    generateAndApplySuggestion,
    replyPath,
    isLoadingPrompts,
    isLoadingReply,
    error,
    appliedPrompt,
    appliedSuggestion,
    reportPoorPrompts,
    rateSuggestion,
    isReportingPoorPrompts,
    hasReportedPoorPrompts,
    appliedSuggestionRating,
    stage,
    hasClickedPreviewReplies,
    setHasClickedPreviewReplies,
    failedSuggestionIndex,
    resetToChooseSuggestion,
  };
};
